import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, Modal } from "@/components/kit";

export default function ClaudeConnector({ open, onClose }) {
  const [tokens, setTokens] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null);
  const [grants, setGrants] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const url = `${window.location.origin}/api/mcp`;

  const load = () => api.get("/mcp-tokens").then((r) => setTokens(r.data.tokens || [])).catch(() => {});
  const loadGrants = () => api.get("/oauth/grants").then((r) => setGrants(r.data.grants || [])).catch(() => setGrants(null));
  useEffect(() => { if (open) { load(); loadGrants(); setFresh(null); setLabel(""); } }, [open]);

  const gen = async () => {
    setBusy(true);
    try { const r = await api.post("/mcp-tokens", { label }); setFresh(r.data); setLabel(""); load(); }
    catch { toast.error("Could not create token"); }
    finally { setBusy(false); }
  };
  const revokeToken = async (tid) => {
    if (!window.confirm("Revoke this token?")) return;
    try { await api.delete(`/mcp-tokens/${tid}`); toast.success("Token revoked"); load(); } catch { toast.error("Could not revoke"); }
  };
  const revokeGrant = async (gid) => {
    if (!window.confirm("Revoke this Claude connection? It stops working immediately.")) return;
    try { await api.post(`/oauth/grants/${gid}/revoke`); toast.success("Connection revoked"); loadGrants(); } catch { toast.error("Could not revoke"); }
  };
  const copy = (t, what) => { navigator.clipboard?.writeText(t); toast.success(`${what} copied`); };

  if (!open) return null;
  const inputCls = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] focus:outline-none focus:border-maroon/50";

  return (
    <Modal open onClose={onClose} title="Connect to Claude" data-testid="claude-connector"
      footer={<Button variant="secondary" onClick={onClose} data-testid="claude-connector-close">Close</Button>}>
      <p className="text-[15px] text-ink/70 mb-4">
        Add Rosemoor as a custom connector in Claude to read and update the CRM in your own words.
        It runs on <span className="font-semibold">your</span> account at your exact permission level —
        anything locked to you in the CRM stays locked in Claude too.
      </p>

      <div className="bg-warm-cream border border-ink/15 rounded-lg p-3 mb-4">
        <div className="text-[12px] font-semibold text-ink/60 uppercase tracking-wide mb-1">Connector URL</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-[13px] break-all text-ink" data-testid="connector-url">{url}</code>
          <button onClick={() => copy(url, "URL")} data-testid="copy-connector-url" className="shrink-0 rounded-lg border border-ink/20 px-2.5 py-1.5 text-[13px] font-semibold hover:bg-ink/5">Copy</button>
        </div>
      </div>

      <ol className="text-[14px] text-ink/75 list-decimal ml-5 space-y-1 mb-4" data-testid="claude-steps">
        <li>In Claude: <span className="font-semibold">Settings → Connectors → Add custom connector</span>.</li>
        <li>Paste the URL above and save. No client ID or secret needed.</li>
        <li>Click <span className="font-semibold">Connect</span> — you'll be sent to Rosemoor to sign in and approve.</li>
        <li>Choose <span className="font-semibold">Allow full access</span> (read + write) or <span className="font-semibold">read-only</span>. All 36 tools then appear in Claude.</li>
      </ol>

      {grants !== null && (
        <div className="border border-ink/15 rounded-lg p-3 mb-4" data-testid="oauth-grants">
          <div className="text-[13px] font-semibold text-ink/70 mb-2">Active Claude connections</div>
          {grants.length === 0 ? (
            <div className="text-[14px] text-ink/50" data-testid="no-grants">No connections yet.</div>
          ) : (
            <div className="space-y-2">
              {grants.map((g) => (
                <div key={g.grant_id} data-testid={`grant-${g.grant_id}`} className="flex items-center justify-between gap-2 bg-warm-cream border border-ink/10 rounded-lg px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="font-semibold text-[14px] truncate">{g.user} · {g.client_name}</div>
                    <div className="text-[12px] text-ink/55">{g.scope?.includes("crm:write") ? "Full access" : "Read-only"} · created {(g.created_at || "").slice(0, 10)}{g.last_used_at ? ` · last used ${(g.last_used_at || "").slice(0, 10)}` : " · never used"}</div>
                  </div>
                  <button onClick={() => revokeGrant(g.grant_id)} data-testid={`revoke-grant-${g.grant_id}`} className="shrink-0 text-[13px] font-semibold text-maroon hover:underline">Revoke</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button onClick={() => setShowAdvanced((v) => !v)} data-testid="toggle-advanced" className="text-[13px] font-semibold text-dark-olive hover:underline mb-2">
        {showAdvanced ? "Hide" : "Advanced:"} personal token for other MCP clients
      </button>
      {showAdvanced && (
        <div className="border border-ink/15 rounded-lg p-3">
          <p className="text-[13px] text-ink/60 mb-2">For non-Claude MCP clients that accept a bearer header. Claude.ai does not use this — it signs in above.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="token-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. My CLI client" className={inputCls} />
            <Button data-testid="generate-token" onClick={gen} disabled={busy} className="shrink-0">{busy ? "Generating…" : "Generate token"}</Button>
          </div>
          {fresh && (
            <div className="bg-cream border-2 border-maroon/40 rounded-lg p-3 mt-3" data-testid="fresh-token">
              <div className="flex items-start gap-2 text-maroon mb-2"><Icon name="KeyRound" className="w-4 h-4 mt-0.5" /><span className="text-[13px] font-semibold">Copy now — shown only once. Send as header: Authorization: Bearer &lt;token&gt;</span></div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[13px] break-all bg-warm-cream rounded px-2 py-1.5" data-testid="fresh-token-value">{fresh.token}</code>
                <button onClick={() => copy(fresh.token, "Token")} data-testid="copy-token" className="shrink-0 rounded-lg border border-ink/20 px-2.5 py-1.5 text-[13px] font-semibold hover:bg-ink/5">Copy</button>
              </div>
            </div>
          )}
          {tokens.length > 0 && (
            <div className="space-y-2 mt-3" data-testid="token-list">
              {tokens.map((t) => (
                <div key={t.token_id} data-testid={`token-${t.token_id}`} className="flex items-center justify-between gap-2 bg-warm-cream border border-ink/10 rounded-lg px-3 py-2">
                  <div className="min-w-0"><div className="font-semibold text-[14px] truncate">{t.label}</div><div className="text-[12px] text-ink/55">Created {(t.created_at || "").slice(0, 10)}</div></div>
                  <button onClick={() => revokeToken(t.token_id)} data-testid={`revoke-${t.token_id}`} className="shrink-0 text-[13px] font-semibold text-maroon hover:underline">Revoke</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
